import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { useScopedAppFlagStore } from '@frontend/scope';

interface UseFormsFreemiumResults {
  isFreemium: boolean;
  freemiumLocationIds: string[]; // array of locationIds with state = FREEMIUM
}
type MappedFlag = { locationId: string; state?: State };

function getFreemiumState(flagsByLocation: Record<string, any>) {
  return Object.entries(flagsByLocation ?? {}).map(([locationId, value]) => ({ locationId, state: value?.state }));
}

const EMPTY_FLAGS_BY_LOCATION: Record<string, { state?: State }> = {};

export const useFormsFreemiumFeatureFlag = (): UseFormsFreemiumResults => {
  const { getFeatureFlagValue, getCustomizationFlagLocationMap } = useScopedAppFlagStore();

  // Currently we are using both FF and CF for freemium.
  //Todo: remove FF once it is fully rolled out.
  const featureEnabled = getFeatureFlagValue('forms-freemium');

  const flagsByLocation = featureEnabled ? getCustomizationFlagLocationMap('forms') : EMPTY_FLAGS_BY_LOCATION;

  const mappedFlags: MappedFlag[] = useMemo(() => getFreemiumState(flagsByLocation), [flagsByLocation]);

  // Filter all locations where state = 'FREEMIUM'
  const freemiumLocationIds = useMemo(() => {
    return mappedFlags.reduce<string[]>((acc, flag) => {
      if (flag.state === State.FREEMIUM) acc.push(flag.locationId);
      return acc;
    }, []);
  }, [mappedFlags]);
  // Currently we are only targeting freemium state for single location
  //Todo: update this logic once we support multi-location
  const isLocationFreemiumEnabled = mappedFlags?.[0]?.state === State.FREEMIUM;

  const isFreemium = featureEnabled && isLocationFreemiumEnabled;

  return { isFreemium, freemiumLocationIds };
};
